import type { FunctionComponent, HTMLAttributes, ReactElement } from 'react';

import type { VolumeControlType } from '../../../store/slices/settings/hooks/useVolumeControl';
import { IconButton, Icons } from '../iconButton/IconButton';
import { RangeInput } from '../rangeInput/RangeInput';
import { Toggle } from '../toggle/Toggle';

import styles from './VolumeSlider.module.css';

export interface VolumeSliderProps extends HTMLAttributes<HTMLDivElement> {
  control: VolumeControlType;
  allIsMuted?: boolean;
  disabled?: boolean;
  masterVolume?: number;
  noVolumeControl?: boolean;
}

const handleClick = (handler: () => void, disabled: boolean): void => {
  if (!disabled) {
    handler();
  }
};

export const VolumeSlider: FunctionComponent<VolumeSliderProps> = ({
  allIsMuted,
  control,
  disabled,
  masterVolume,
  noVolumeControl,
  ...rest
}): ReactElement => {
  const { value, toggle, unmuteVideo, change, temporaryChange } = control;

  if (noVolumeControl) {
    return (
      <div className={styles.volumeSlider} {...rest}>
        <Toggle
          disabled={disabled}
          checked={value > 0 && masterVolume !== undefined && masterVolume > 0}
          onChange={(event) => change(event.target.checked ? 1 : 0)}
        />
      </div>
    );
  }

  const disableSlider = (masterVolume === 0 && !allIsMuted) || (disabled ? disabled : false);
  return (
    <div className={styles.volumeSlider} {...rest}>
      <IconButton
        ghost
        icon={value > 0 ? Icons.SoundOn : Icons.SoundOff}
        onClick={(): void => {
          toggle();
          handleClick(unmuteVideo, disableSlider);
        }}
        disabled={disableSlider}
        className={styles.volumeSlider__button}
      />
      <div className={styles.volumeSlider__sliderContainer}>
        <RangeInput
          disabled={disableSlider}
          max={1}
          min={0}
          onChange={(newValue) => change(newValue)}
          onClick={() => handleClick(unmuteVideo, disableSlider)}
          onDrag={(newValue) => temporaryChange(newValue)}
          step={0.1}
          value={value}
        />
      </div>
    </div>
  );
};
